import { useMemo } from 'react';
import { FarmProduct } from 'public/context/CartContext';
import {CouponDiscounts} from "../../../../interfaces/Coupon";

export interface CouponVatSaving {
  vatRate: number;
  vatSaved: number;
}

export interface CouponSummary {
  totalDiscount: number; // Total coupon discount across all farms
  vatSavings: CouponVatSaving[]; // VAT savings per VAT rate
  discountByFarm: Record<number, number>; // Discount amount grouped by farm ID
}

const useCouponSummary = (
  farmProducts: FarmProduct[],
  coupons: CouponDiscounts[]
): CouponSummary => {
  return useMemo(() => {
    let totalDiscount = 0;
    const vatSavingsMap = new Map<number, number>();
    const discountByFarm: Record<number, number> = {};

    for (const farm of farmProducts) {
      let farmDiscount = 0;
      for (const product of farm.products) {
        const productId = product.cartProduct?.id;
        if (!productId) continue;

        const amount = product.amount;
        // sum per-unit coupon discounts for this product
        const perUnitDiscount = coupons.reduce(
          (sum, c) => sum + (c.productDiscounts?.[productId] ?? 0),
          0
        );
        const discount = perUnitDiscount * amount;
        if (discount <= 0) continue;

        farmDiscount += discount;
        const vatRate = product.vat ?? 0;
        const vatSaved = discount * (vatRate / 100);
        vatSavingsMap.set(vatRate, (vatSavingsMap.get(vatRate) ?? 0) + vatSaved);
      }
      discountByFarm[farm.farm_id] = farmDiscount;
      totalDiscount += farmDiscount;
    }

    const vatSavings: CouponVatSaving[] = Array.from(vatSavingsMap, ([vatRate, vatSaved]) => ({ vatRate, vatSaved }));

    return { totalDiscount, vatSavings, discountByFarm };
  }, [farmProducts, coupons]);
};

export default useCouponSummary;
